/**
 * usePostureBrief — derives the editorial copy block at the top of the
 * Command Center from the active posture state.
 *
 * Phase 11 wires real templated copy into the standfirst. The hook signature
 * stays stable for callers — Phase 11 only widens the optional inputs so the
 * Dashboard can pass through delta + topAction + criticalCount when they're
 * available. Existing callers that pass only `{score, pillarScores}` keep
 * working unchanged.
 */

import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  composePostureBrief,
  type Pillar,
  type PostureBrief,
  type PostureBriefFacts,
  type PostureBriefPillarScores,
} from '@/lib/posture-brief'
import type { ScanScheduleState } from '@/lib/brief-helpers'
import type { ActionBriefFields } from '@/lib/brief-shape'

export interface UsePostureBriefArgs {
  /**
   * Active tenant ID. Kept for call-site compatibility; the composer is
   * deterministic (same input, same brief) and no longer varies copy per tenant.
   */
  tenantId?: string
  /** Current Guardian Score (0-100). */
  score?: number | null
  /** Pillar component scores. */
  pillarScores?: PostureBriefPillarScores
  /** 30-day score delta — drives the delta tag, the verdict, the headline and the lede. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact priority action — informs the headline, lede, verdict and primary action. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity issue count — drives the headline and verdict. */
  criticalCount?: number
  /**
   * Pillar facts for the action-first lede (blueprint §6.2 row 1), from
   * queries the Dashboard already runs: `identity` from user insights
   * (`noMfaEnabledUsers`, `noMfaGlobalAdmins`), `bpa` from the violation
   * summary (open and critical counts), `compliance` from dashboard stats
   * (`activeDrifts`). Leave a field undefined while its query loads.
   */
  facts?: PostureBriefFacts
  /** The top priority action's href; adds the brief's `primaryAction`. With
   *  `noData`, pass the Ad-Hoc Scans route only for a viewer who may run a scan. */
  primaryHref?: string
  /** Score is null because no assessment exists yet, not because a query is loading. */
  noData?: boolean
  /** With 
oData: Tenant Management, only for a viewer who may edit the scan schedule. */
  scheduleSettingsHref?: string
  /** With 
oData: the tenant's scan schedule (`useTenantScanSchedule`). */
  scanSchedule?: ScanScheduleState
}

export function usePostureBrief(args: UsePostureBriefArgs = {}): PostureBrief & ActionBriefFields {
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Memoize on the value-shape rather than the object identity so the
  // Dashboard can rebuild `args` each render without retriggering composition.
  const deltaScore = args.deltas?.score
  const deltaDays = args.deltas?.days
  const topActionPillar = args.topAction?.pillar
  const topActionTitle = args.topAction?.title
  const topActionImpact = args.topAction?.impact
  const noMfaMembers = args.facts?.identity?.noMfaMembers
  const noMfaGlobalAdmins = args.facts?.identity?.noMfaGlobalAdmins
  const bpaFailing = args.facts?.bpa?.failing
  const bpaCritical = args.facts?.bpa?.critical
  const driftedResources = args.facts?.compliance?.driftedResources

  return useMemo(
    () =>
      composePostureBrief({
        userEmail,
        tenantId: args.tenantId,
        score: args.score,
        pillarScores: args.pillarScores,
        deltas:
          deltaScore !== undefined || deltaDays !== undefined
            ? { score: deltaScore, days: deltaDays }
            : undefined,
        topAction:
          topActionPillar && topActionTitle
            ? { pillar: topActionPillar, title: topActionTitle, impact: topActionImpact }
            : undefined,
        criticalCount: args.criticalCount,
        facts: {
          identity: { noMfaMembers, noMfaGlobalAdmins },
          bpa: { failing: bpaFailing, critical: bpaCritical },
          compliance: { driftedResources },
        },
        primaryHref: args.primaryHref,
        noData: args.noData,
        scheduleSettingsHref: args.scheduleSettingsHref,
        scanSchedule: args.scanSchedule,
      }),
    [
      userEmail,
      args.tenantId,
      args.score,
      args.pillarScores,
      deltaScore,
      deltaDays,
      topActionPillar,
      topActionTitle,
      topActionImpact,
      args.criticalCount,
      noMfaMembers,
      noMfaGlobalAdmins,
      bpaFailing,
      bpaCritical,
      driftedResources,
      args.primaryHref,
      args.noData,
      args.scheduleSettingsHref,
      args.scanSchedule,
    ],
  )
}
